import { useCallback, useEffect, useRef } from 'react';

import { useMarkLeadAsReadMutation } from '@app/api/Leads/api';
import type { ModelLead } from '@app/api/Leads/types';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';

interface UseLeadUnreadStateParams {
  activeBranchId: number | null;
  isLeadCardDataActual: boolean;
  leadId: number | null;
  unreadMessage: string | null | undefined;
  onLeadUpdated(lead: ModelLead): void;
}

export const useLeadUnreadState = ({
  activeBranchId,
  isLeadCardDataActual,
  leadId,
  unreadMessage,
  onLeadUpdated,
}: UseLeadUnreadStateParams) => {
  const { showNotification } = useNotification();
  const [markLeadAsRead] = useMarkLeadAsReadMutation();
  const activeEntryLeadIdRef = useRef<number | null>(null);
  const autoReadHandledLeadIdRef = useRef<number | null>(null);
  const manualUnreadLeadIdRef = useRef<number | null>(null);

  // Manual-unread protection belongs to the current explicit card entry only.
  // StrictMode can re-run this effect, so the tracked id prevents false resets.
  useEffect(() => {
    if (activeEntryLeadIdRef.current === leadId) return;

    activeEntryLeadIdRef.current = leadId;
    manualUnreadLeadIdRef.current = null;
  }, [leadId]);

  const applyManualUnreadLeadUpdate = useCallback(
    (updatedLead: ModelLead) => {
      manualUnreadLeadIdRef.current = updatedLead.Id;
      onLeadUpdated(updatedLead);
    },
    [onLeadUpdated]
  );

  useEffect(() => {
    if (!activeBranchId || leadId === null || leadId <= 0) return;
    if (!isLeadCardDataActual) return;
    if (autoReadHandledLeadIdRef.current === leadId) return;

    autoReadHandledLeadIdRef.current = leadId;

    if (!unreadMessage || manualUnreadLeadIdRef.current === leadId) return;

    const markCurrentLeadAsRead = async () => {
      try {
        const updatedLead = await markLeadAsRead({
          ActiveBranchId: activeBranchId,
          leadId,
        }).unwrap();

        onLeadUpdated(updatedLead);
      } catch (error) {
        showNotification(error);
      }
    };

    void markCurrentLeadAsRead();
  }, [
    activeBranchId,
    isLeadCardDataActual,
    leadId,
    markLeadAsRead,
    onLeadUpdated,
    showNotification,
    unreadMessage,
  ]);

  return { applyManualUnreadLeadUpdate };
};
